/**
 * Sizes
 */
export enum EMealSize {
	ALL = 'all',
	S_SIZE = 'sizeS',
	M_SIZE = 'sizeM',
	L_SIZE = 'sizeL',
	XL_SIZE = 'sizeXL',
}

export enum EMealSizeWithoutAll {
	S_SIZE = 'sizeS',
	M_SIZE = 'sizeM',
	L_SIZE = 'sizeL',
	XL_SIZE = 'sizeXL',
}

export const MealSizeMap: Record<EMealSize, string> = {
	[EMealSize.ALL]: 'Wszystkie',
	[EMealSize.S_SIZE]: 'Rozmiar S',
	[EMealSize.M_SIZE]: 'Rozmiar M',
	[EMealSize.L_SIZE]: 'Rozmiar L',
	[EMealSize.XL_SIZE]: 'Rozmiar XL',
}

export const MealSizeSortOrder: string[] = [EMealSize.S_SIZE, EMealSize.M_SIZE, EMealSize.L_SIZE, EMealSize.XL_SIZE]

const compareByMealSizeOrder = (codeA: string, codeB: string): number => {
	const indexA = MealSizeSortOrder.indexOf(codeA)
	const indexB = MealSizeSortOrder.indexOf(codeB)
	if (indexA === -1 && indexB === -1) return codeA.localeCompare(codeB)
	if (indexA === -1) return 1
	if (indexB === -1) return -1
	return indexA - indexB
}

export const sortMealSizes = <T extends { code: string }>(sizes: T[]): T[] =>
	[...sizes].sort((a, b) => compareByMealSizeOrder(a.code || '', b.code || ''))

export const sortMealVariantsBySize = <T extends { mealSizeId: number }, S extends { id: number; code: string }>(
	variants: T[],
	sizes: S[] | undefined,
): T[] => {
	const getCode = (variant: T): string => sizes?.find(s => s.id === variant.mealSizeId)?.code ?? ''

	return [...variants].sort((a, b) => compareByMealSizeOrder(getCode(a), getCode(b)))
}

/**
 * Types
 */
export enum EMealType {
	BREAKFAST = 'breakfast',
	SECOND_BREAKFAST = 'second_breakfast',
	LUNCH = 'lunch',
	AFTERNOON_SNACK = 'afternoon_snack',
	DINNER = 'dinner',
	POST_WORKOUT = 'post_workout',
}

export const EMealTypeAsArray = Object.values(EMealType)

export const MealTypeMap: Record<EMealType, string> = {
	[EMealType.BREAKFAST]: 'Śniadanie',
	[EMealType.SECOND_BREAKFAST]: 'Drugie śniadanie',
	[EMealType.LUNCH]: 'Obiad',
	[EMealType.AFTERNOON_SNACK]: 'Podwieczorek',
	[EMealType.DINNER]: 'Kolacja',
	[EMealType.POST_WORKOUT]: 'Potreningowy',
}

export const MealTypeSortOrder: EMealType[] = [
	EMealType.BREAKFAST,
	EMealType.SECOND_BREAKFAST,
	EMealType.LUNCH,
	EMealType.AFTERNOON_SNACK,
	EMealType.DINNER,
	EMealType.POST_WORKOUT,
]

export const MealTypesSelect = generateSelectOptions(EMealType, MealTypeMap)

export const KidsMealTypeActiveSizeMap: Partial<Record<EMealType, EMealSize>> = {
	[EMealType.BREAKFAST]: EMealSize.M_SIZE,
	[EMealType.SECOND_BREAKFAST]: EMealSize.S_SIZE,
	[EMealType.LUNCH]: EMealSize.S_SIZE,
	[EMealType.AFTERNOON_SNACK]: EMealSize.S_SIZE,
	[EMealType.DINNER]: EMealSize.M_SIZE,
}

export enum EProductLine {
	FOODIFY = 'foodify',
	SPORT = 'sport',
}

export const ProductLineMap: Record<EProductLine, string> = {
	[EProductLine.FOODIFY]: 'Foodify',
	[EProductLine.SPORT]: 'Sport',
}

export const ProductLineSelect = generateSelectOptions(EProductLine, ProductLineMap)

/**
 * Component types
 */
export enum EDietSharedComponentType {
	SEMIPRODUCT = 'dietary_semi_product',
	MATERIAL = 'dietary_raw_material',
	GROUP = 'dietary_group',
}

/**
 * Others
 */
export enum EMealKind {
	MEAL = 'meal',
	MEALPREP = 'prep_meal',
	ADDON = 'addon',
}

export enum EMealSubKind {
	DEFAULT = 'default',
	KIDS = 'kids',
	FOODPACK = 'foodpack',
	ADDON = 'addon',
}

export const MealSubkindMap: Record<EMealSubKind, string> = {
	[EMealSubKind.DEFAULT]: 'Domyślne',
	[EMealSubKind.KIDS]: 'Dania dla dzieci',
	[EMealSubKind.FOODPACK]: 'Foodpack',
	[EMealSubKind.ADDON]: 'Dodatek',
}

export const MealSubkindsSelect = generateSelectOptions(EMealSubKind, MealSubkindMap)

export enum EDietMealSearchType {
	MEAL = 'meal',
	MEALPREP = 'prep_meal',
	ADDONS = 'addons',
	ADDON_MEAL = 'addon_meal',
	ADDON_MEAL_PREP = 'addon_prep_meal',
	ALL_ADDONS = 'all_addons',
}

export enum EMealCategory {
	MEAL = 'meal',
	MEALPREP = 'prep_meal',
	ADDON = 'addon',
}

export enum EMealHistoryType {
	MEAL = 'meal',
	ADDON = 'addon',
	DIET = 'diet',
}

export enum EDietSharedMealPackagingType {
	SEMIPRODUCT = 'dietary_semi_product',
	MATERIAL = 'dietary_raw_material',
	GROUP = 'dietary_group',
}

/**
 * Dialog
 */
export enum EDietSharedDialogType {
	MEAL = 'meal',
	MEALPREP = 'prep_meal',
	ADDON_MEAL = 'addon_meal',
	ADDON_MEAL_PREP = 'addon_prep_meal',
}

export enum EDietSharedDialogTabs {
	CLIENT_INFO = 'client_info',
	INGRIDIENTS_AND_VARIANTS = 'ingridients_and_variants',
	PRODUCTION_STAGES = 'production_stages',
	MEAL_REVIEWS = 'meal_reviews',
	OCCURANCE_HISTORY = 'occurance_history',
	EDIT_HISTORY = 'edit_history',
	RELATED_MEALS = 'related_meals',
	RELATED_VARIANTS = 'related_variants',
	MENU_USAGE_DATES = 'menu_usage_dates',
}

export const DietarySharedDialogTypeToRelatedSearchType: Record<EDietSharedDialogType, EDietMealSearchType> = {
	[EDietSharedDialogType.MEAL]: EDietMealSearchType.MEALPREP,
	[EDietSharedDialogType.MEALPREP]: EDietMealSearchType.MEAL,
	[EDietSharedDialogType.ADDON_MEAL]: EDietMealSearchType.ADDON_MEAL_PREP,
	[EDietSharedDialogType.ADDON_MEAL_PREP]: EDietMealSearchType.ADDON_MEAL,
}

export const DietarySharedDialogEditStatusKeys = {
	[EDietSharedDialogType.MEAL]: IDietStoreModelKey.MEALS,
	[EDietSharedDialogType.ADDON_MEAL]: IDietStoreModelKey.MEALS,
	[EDietSharedDialogType.MEALPREP]: IDietStoreModelKey.MEALPREPS,
	[EDietSharedDialogType.ADDON_MEAL_PREP]: IDietStoreModelKey.MEALPREPS,
}

/**
 * Search components
 */
export enum EDietSearchComponentsFilter {
	NAME = 'name',
	INTERNAL_NAME = 'internal_name',
	WAREHOUSE_NAME = 'warehouse_name',
}

/**
 * Precyzja wag komponentów — spójna z decimal(10,4) w bazie.
 * 2 miejsca gubią WOT odtwarzany z ilorazu WpOT/WpOM przy małych gramaturach (np. sól 0.09 g).
 */
export const WEIGHT_DECIMAL_PRECISION = 4
